import { useMemo, useState } from "react";
import {
    generateCatalogDescription,
    generateCatalogHeader1,
    generateCatalogTitle
} from "@features/seo";
import { configApi } from "@shared/lib/configApi";
import { getBreadcrumbCatalogSchema } from "@shared/lib/utils/getBreadcrumbCatalogSchema";
import { getCatalogCarSchema } from "@shared/lib/utils/getCatalogCarSchema";
import { DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";
import { SEODataParams } from "./types";

export const useSEOData = ({
    preloadedProps,
    brandValue,
    modelValue,
    generationValue,
    citiesValue,
    country,
    ssrQuantity,
    currentPage,
    totalPages,
    canonicalPath
}: SEODataParams) => {
    const ssrOffers = preloadedProps?.preloadOffers || [];
    const ssrOgImage = preloadedProps?.preloadOffers?.[0]?.imagesList?.[0]?.big || null;

    const [header1, setHeader1] = useState(
        generateCatalogHeader1({
            brand: brandValue?.name,
            model: modelValue?.name,
            generation: generationValue?.name,
            qnt: ssrQuantity,
            city: citiesValue[0]?.name
        })
    );

    const description = useMemo(
        () =>
            generateCatalogDescription({
                brand: brandValue?.name,
                model: modelValue?.name,
                generation: generationValue?.name,
                city: citiesValue[0]?.name,
                qnt: ssrQuantity,
                country
            }),
        [brandValue, modelValue, generationValue, citiesValue, ssrQuantity, country]
    );

    const titleTagContent = useMemo(
        () =>
            generateCatalogTitle({
                brand: brandValue?.name,
                model: modelValue?.name,
                generation: generationValue?.name,
                city: citiesValue[0]?.name,
                qnt: ssrQuantity,
                country
            }),
        [brandValue, modelValue, generationValue, citiesValue, ssrQuantity, country]
    );

    const keywords = useMemo(
        () =>
            [
                "авто",
                "auto",
                "продажа авто",
                "автомобили",
                "автосалоны",
                "авторынок",
                "базар авто",
                "каталог автомобилей",
                brandValue?.name,
                modelValue?.name,
                generationValue?.name
            ]
                .filter(Boolean)
                .join(", "),
        [brandValue, modelValue, generationValue]
    );

    const metaRobots = currentPage === 1 ? ("index,follow" as const) : ("noindex,follow" as const);
    const canonicalUrl = `${configApi.API_PUBLIC_DOMAIN}${canonicalPath}`;
    const canonicalWithPage =
        currentPage === 1 ? canonicalUrl : `${canonicalUrl}?page=${currentPage}`;
    const linkRelPrev = currentPage > 1 ? `${canonicalUrl}?page=${currentPage - 1}` : null;
    const linkRelNext = currentPage < totalPages ? `${canonicalUrl}?page=${currentPage + 1}` : null;

    const breadcrumbSchema = useMemo(
        () => getBreadcrumbCatalogSchema(brandValue, modelValue),
        [brandValue, modelValue]
    );

    const productsCarSchema = useMemo(
        () =>
            brandValue
                ? getCatalogCarSchema({
                      canonicalUrl: canonicalWithPage,
                      title: titleTagContent,
                      description: description,
                      brandName: brandValue?.name,
                      offersData: ssrOffers,
                      totalOffers: ssrQuantity
                  })
                : null,
        [brandValue, canonicalWithPage, titleTagContent, description, ssrOffers, ssrQuantity]
    );

    return {
        header1,
        setHeader1,
        description,
        titleTagContent,
        keywords,
        metaRobots,
        canonicalWithPage,
        linkRelPrev,
        linkRelNext,
        breadcrumbSchema,
        productsCarSchema,
        ssrOgImage: ssrOgImage || DEFAULT_OG_IMAGE
    };
};